"use client"

import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react"
import useToggleState from "@lib/hooks/use-toggle-state"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { clx } from "@modules/common/components/ui"
import { Fragment } from "react"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"
import { Locale } from "@lib/data/locales"

const HelpItems = {
  "Tu cuenta": "/account",
  "Tus pedidos": "/account/orders",
  "Tus direcciones": "/account/addresses",
  "Cesta": "/cart",
}

type SideMenuProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
  categories: { name: string; handle: string }[]
}

const SideMenu = ({ regions, locales, currentLocale, categories }: SideMenuProps) => {
  const countryToggleState = useToggleState()
  const languageToggleState = useToggleState()

  return (
    <Popover className="flex">
      {({ open, close }) => (
        <>
          <PopoverButton
            data-testid="nav-menu-button"
            className="h-10 w-10 flex items-center justify-center text-zl-ink hover:text-zl-red focus:outline-none"
            aria-label="Menú"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6" aria-hidden>
              <path d="M3 7h18M3 12h18M3 17h18" strokeLinecap="square" />
            </svg>
          </PopoverButton>

          {open && (
            <div
              className="fixed inset-0 z-[60] bg-zl-black/40"
              onClick={close}
              data-testid="side-menu-backdrop"
            />
          )}

          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <PopoverPanel
              static
              className="fixed left-0 top-0 bottom-0 z-[61] w-[85vw] max-w-[365px] bg-white text-zl-ink border-r border-zl-line shadow-drawer overflow-y-auto whitespace-normal"
              data-testid="nav-menu-popup"
            >
              <div className="h-16 px-6 border-b border-zl-line flex items-center justify-between">
                <span className="font-bold text-[22px] tracking-[-0.045em]">
                  zeiolabs<span className="text-zl-red">.</span>
                </span>
                <button data-testid="close-menu-button" onClick={close} aria-label="Cerrar menú">
                  <XMark />
                </button>
              </div>

              <div className="py-2">
                <h3 className="px-6 pt-4 pb-2 zl-badge text-zl-steel">Catálogo</h3>
                <ul>
                  {categories.map((c) => (
                    <li key={c.handle}>
                      <LocalizedClientLink
                        href={`/categories/${c.handle}`}
                        onClick={close}
                        className="flex items-center justify-between px-6 py-3 border-b border-zl-line font-display text-xl font-medium tracking-[-0.01em] hover:text-zl-red"
                      >
                        <span>{c.name}</span>
                        <ArrowRightMini />
                      </LocalizedClientLink>
                    </li>
                  ))}
                  <li>
                    <LocalizedClientLink
                      href="/store"
                      onClick={close}
                      className="flex items-center justify-between px-6 py-3 zl-label hover:text-zl-red"
                    >
                      Ver todo el catálogo
                      <ArrowRightMini />
                    </LocalizedClientLink>
                  </li>
                </ul>
              </div>

              <div className="border-t border-zl-line py-2 mt-2">
                <h3 className="px-6 pt-4 pb-2 zl-badge text-zl-steel">Cuenta y ajustes</h3>
                <ul>
                  {Object.entries(HelpItems).map(([name, href]) => (
                    <li key={name}>
                      <LocalizedClientLink
                        href={href}
                        onClick={close}
                        className="block px-6 py-2.5 text-sm hover:text-zl-red"
                      >
                        {name}
                      </LocalizedClientLink>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col gap-y-4 px-6 py-3 text-sm">
                  {!!locales?.length && (
                    <div
                      className="flex justify-between"
                      onMouseEnter={languageToggleState.open}
                      onMouseLeave={languageToggleState.close}
                    >
                      <LanguageSelect
                        toggleState={languageToggleState}
                        locales={locales}
                        currentLocale={currentLocale}
                      />
                      <ArrowRightMini
                        className={clx(
                          "transition-transform duration-150",
                          languageToggleState.state ? "-rotate-90" : ""
                        )}
                      />
                    </div>
                  )}
                  <div
                    className="flex justify-between"
                    onMouseEnter={countryToggleState.open}
                    onMouseLeave={countryToggleState.close}
                  >
                    {regions && (
                      <CountrySelect toggleState={countryToggleState} regions={regions} />
                    )}
                    <ArrowRightMini
                      className={clx(
                        "transition-transform duration-150",
                        countryToggleState.state ? "-rotate-90" : ""
                      )}
                    />
                  </div>
                </div>
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  )
}

export default SideMenu
